import type { StaticEntityType } from "@config";
import Checkbox from "@components/ui/checkbox";
import { cn } from "@lib/cn";
import { Slide } from "@library-containers/layers";
import {
  slidePreviewStyles,
  useSlidesLibraryStoreShallow,
} from "@modules/slides-library/shared";
import { IconArrowLeft } from "@tabler/icons-react";
import { memo, useCallback, useEffect, useState } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { useStorySlides } from "../hooks";
import { SlidesLibraryContentSkeleton } from "@modules/slides-library/shared/components/skeleton/slides-library-content-skeleton";

const hashContent = (slide: StaticEntityType) => {
  const str = JSON.stringify(slide.settings ?? {});
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
};

const getSlideId = (slide: StaticEntityType, storyId: string) =>
  slide.id || `${storyId}-${hashContent(slide)}`;

const selectedBoxShadow =
  "0px 21px 9px rgba(10,10,10,0.01), 0px 12px 7px rgba(10,10,10,0.04), 0px 5px 5px rgba(10,10,10,0.06), 0px 1px 3px rgba(10,10,10,0.07)";
const unselectedBoxShadow =
  "0px 5px 2px rgba(10,10,10,0.01), 0px 3px 2px rgba(10,10,10,0.03), 0px 1px 1px rgba(10,10,10,0.04), 0px 0px 1px rgba(10,10,10,0.05)";

export const StorySlidesView = () => {
  const {
    storyId,
    storyTitle,
    contentPointer,
    selectedKind,
    clearImportStoriesSelectedStory,
    handleMultiselectSlide,
    isSelected,
    selectedSlideIds,
  } = useSlidesLibraryStoreShallow((s) => ({
    storyId: s.importStoriesSelectedStoryId,
    storyTitle: s.importStoriesSelectedStoryTitle,
    contentPointer: s.importStoriesContentPointer,
    selectedKind: s.importStoriesSelectedKind,
    clearImportStoriesSelectedStory: s.clearImportStoriesSelectedStory,
    handleMultiselectSlide: s.handleMultiselectSlide,
    isSelected: s.isSelected,
    selectedSlideIds: s.selectedSlideIds,
  }));

  const { data: slides, isLoading } = useStorySlides(
    storyId,
    contentPointer || undefined,
    selectedKind,
  );

  const handleAddAllSlides = () => {
    if (!slides) return;
    for (const slide of slides) {
      const slideId = getSlideId(slide, storyId);
      if (!isSelected(slideId)) {
        handleMultiselectSlide({ id: slideId, slide, type: "saved" }, "add");
      }
    }
  };

  const handleToggleSlide = useCallback(
    (slide: StaticEntityType) => {
      const slideId = getSlideId(slide, storyId);
      const selected = selectedSlideIds.has(slideId);
      handleMultiselectSlide(
        { id: slideId, slide, type: "saved" },
        selected ? "remove" : "add",
      );
    },
    [storyId, selectedSlideIds, handleMultiselectSlide],
  );

  return (
    <div className="tw-flex tw-h-full tw-flex-col">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4">
        <div className="tw-flex tw-items-center tw-gap-3">
          <button
            type="button"
            onClick={clearImportStoriesSelectedStory}
            aria-label="Back to stories"
            className="tw-bg-transparent tw-flex tw-size-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-p-0 tw-outline-none hover:tw-bg-blue-50"
          >
            <IconArrowLeft
              size={16}
              stroke={1.5}
              aria-hidden="true"
              className="tw-fill-none tw-text-blue"
            />
          </button>
          <span className="tw-font-sans tw-text-sm tw-font-semibold tw-text-blue">
            {storyTitle}
          </span>
        </div>
        <button
          type="button"
          onClick={handleAddAllSlides}
          aria-label={`Add all slides from ${storyTitle}`}
          className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-bg-white tw-px-3 tw-font-sans tw-text-sm tw-font-medium tw-text-blue tw-shadow-2xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-100 hover:tw-bg-blue-50"
        >
          Add all slides
        </button>
      </div>

      {/* Slides grid */}
      {isLoading ? (
        <SlidesLibraryContentSkeleton variant="story-slides" />
      ) : (
        <div className="tw-flex tw-flex-1 tw-flex-wrap tw-content-start tw-gap-4 tw-px-6 tw-pb-4">
          {slides?.map((slide) => {
            const slideId = getSlideId(slide, storyId);
            return (
              <StorySlideItem
                key={slideId}
                slide={slide}
                selected={selectedSlideIds.has(slideId)}
                onToggle={handleToggleSlide}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const StorySlideItem = memo(
  ({
    slide,
    selected,
    onToggle,
  }: {
    slide: StaticEntityType;
    selected: boolean;
    onToggle: (slide: StaticEntityType) => void;
  }) => {
    const { ref, isIntersecting } = useIntersectionObserver({
      threshold: 0.3,
    });
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
      if (isIntersecting && slide) {
        setShouldRender(true);
        return;
      }
      if (!isIntersecting) {
        const timeout = setTimeout(() => setShouldRender(false), 200);
        return () => clearTimeout(timeout);
      }
    }, [isIntersecting, slide]);

    const settings =
      slide.settings && typeof slide.settings === "object"
        ? (slide.settings as Record<string, unknown>)
        : undefined;
    const title =
      typeof settings?.title === "string" ? settings.title : undefined;

    return (
      <div
        ref={ref}
        className="tw-group/slide tw-relative tw-flex tw-w-[352px] tw-cursor-pointer tw-flex-col focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200"
        onClick={() => onToggle(slide)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle(slide);
          }
        }}
        role="button"
        tabIndex={0}
        aria-pressed={selected}
      >
        {/* Slide preview — 352 × 198 (16:9) */}
        <div
          className={cn(
            "tw-relative tw-h-[198px] tw-w-[352px] tw-rounded-lg tw-bg-white tw-outline tw-outline-1 !tw-outline-offset-0 tw-transition-[outline-color,box-shadow]",
            selected
              ? "tw-outline-primary"
              : "tw-outline-blue-200 group-hover/slide:tw-outline-primary",
          )}
          style={
            {
              "--shadow-default": unselectedBoxShadow,
              "--shadow-active": selectedBoxShadow,
              // 770px screenFit slide fitted to 198px frame, ~0.5% bleed.
              "--slide-preview-scaled-size": "388.89%",
              "--slide-preview-scale": "0.25843",
              boxShadow: selected
                ? selectedBoxShadow
                : "var(--slide-shadow, var(--shadow-default))",
            } as React.CSSProperties
          }
          onMouseEnter={(e) =>
            !selected &&
            e.currentTarget.style.setProperty(
              "--slide-shadow",
              selectedBoxShadow,
            )
          }
          onMouseLeave={(e) =>
            !selected && e.currentTarget.style.removeProperty("--slide-shadow")
          }
        >
          <span className="tw-absolute tw-inset-0 tw-z-1 tw-size-full" />
          <div className="tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-lg">
            <div
              className={cn(
                slidePreviewStyles.scaled,
                "tw-pointer-events-none tw-grid tw-place-items-center",
              )}
            >
              <div className="tw-h-full tw-w-full">
                <div className="story tw-h-full">
                  <div className="storyContent tw-h-full">
                    <div
                      className={cn(
                        "tw-h-full tw-transition-opacity tw-duration-200",
                        isIntersecting ? "tw-opacity-100" : "tw-opacity-0",
                      )}
                    >
                      {shouldRender && slide && (
                        <Slide entity={slide as StaticEntityType} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Checkbox */}
          <div
            className={cn(
              "tw-absolute tw-left-2 tw-top-2 tw-z-10",
              selected ? "tw-block" : "tw-hidden group-hover/slide:tw-block",
            )}
          >
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggle(slide)}
            >
              <Checkbox.Toggle
                className={cn(
                  "tw-size-4 tw-rounded !tw-border-0 tw-shadow-xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue/5 [&_i]:tw-size-3 [&_i]:tw-text-xs/none",
                  !selected && "tw-bg-white",
                )}
              />
            </Checkbox>
          </div>
        </div>

        {title && (
          <span className="tw-mt-3 tw-block tw-font-sans tw-text-sm tw-font-normal tw-text-blue">
            {title}
          </span>
        )}
      </div>
    );
  },
);
StorySlideItem.displayName = "StorySlideItem";
